import React, { useState } from 'react'
import LobbyPanel from './LobbyPanel'
import { useGame } from '@/app/providers/GameContextProvider';
import { AiOutlineCopy } from 'react-icons/ai';
import IconGlowWrapper from '@/app/components/IconGlowWrapper';
import { GiExitDoor } from 'react-icons/gi';
import { FaMagnifyingGlass } from 'react-icons/fa6';
import { FaCheck } from 'react-icons/fa';
import TooltipFFXIV from '@/app/components/TooltipFFXIV';
import TextFFXIV from '@/app/components/TextFFXIV';
import { createGameRoom, joinGameRoom } from '@/lib/utils/roomUtils';
import { useTranslation } from '@/hooks/useTranslation';
import { useGlobalPopup } from '@/app/providers/GlobalPopupProvider';

const LobbyInfo = () => {
    const { room, client, setRoom, leaveRoom, gameState } = useGame();
    const { t } = useTranslation();
    const roomId = room ? room.roomId : "N/A";
    const { showPopup } = useGlobalPopup();

    const [isEditing, setIsEditing] = useState(false);
    const [inputRoomId, setInputRoomId] = useState(roomId);

    const isAlone = gameState && (gameState.players?.size ?? 0) <= 1;



    const handleCopy = () => {
        const url = `${window.location.origin}${window.location.pathname}?id=${roomId}`;
        navigator.clipboard.writeText(url);
        showPopup({ type: 'success', message: t("lobby.lobbyInviteLinkCopied") });
    }

    const handleJoin = async (id: string) => {
        setIsEditing(false);
        let lobbyId = id.trim();
        // Failsafe: extract id from URL if pasted
        try {
            const urlMatch = lobbyId.match(/[?&]id=([\w-]+)/);
            if (urlMatch) {
                lobbyId = urlMatch[1];
            }
        } catch { }
        if (client && lobbyId !== "") {
            try {
                const joinedRoom = await joinGameRoom(client, lobbyId);
                showPopup({ type: 'success', message: t("lobby.lobbyJoined") });
                setRoom(joinedRoom);
                setInputRoomId(joinedRoom.roomId);
            } catch {
                showPopup({ type: 'error', message: t("lobby.lobbyDoesntExist") });
            }
        }
    };

    const handleLeave = async () => {
        if (client && room) {
            try {
                // Leave current room (using leaveRoom to mark as intentional)
                leaveRoom();
                // Create a new room for the user
                const newRoom = await createGameRoom(client);
                console.log(newRoom.state)
                showPopup({ type: 'success', message: t("lobby.lobbyLeft") });
                setRoom(newRoom);
                setInputRoomId(newRoom.roomId);
            } catch {
                showPopup({ type: 'error', message: t("lobby.failedToLeaveLobby") });
            }
        }
    };



    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            setIsEditing(false);
            handleJoin(inputRoomId);
        }
    };

    const handleInputBlur = () => {
        setIsEditing(false);
        handleJoin(inputRoomId);
    };

    return (
        <LobbyPanel name={t("lobby.lobby")} className="rounded w-full md:w-fit">
            <div className="flex items-center md:mt-1">
                <div className="font-contrax shadow-[0_0px_5px_#fdba74,0_0_6px_0_#fdba74_inset] border border-[#D9A876] rounded w-[20px] h-[20px] xl:w-[30px] xl:h-[30px] flex items-center justify-center">
                    <div className="text-xs xl:text-base translate-y-[1px] text-shadow shadow-[#F6CD69]/50 text-[#F6CD69] font-contrax">ID</div>
                </div>
                {!isEditing ? (
                    <>
                        <div className="text-shadow shadow-blue-800 font-myriad text-xs xl:text-xl whitespace-nowrap ml-3">{roomId}</div>
                        <div className="group relative">
                            <IconGlowWrapper
                                icon={<AiOutlineCopy />}
                                onClick={handleCopy}
                                className="text-[16px] xl:text-[24px] ml-2 xl:ml-3"
                            />
                            <TooltipFFXIV>
                                <TextFFXIV text={t("lobby.copyLobbyInviteUrl")} />
                            </TooltipFFXIV>
                        </div>
                        <div className="group relative">
                            {isAlone ? (
                                <div className="group relative join-lobby-block">
                                    <IconGlowWrapper
                                        icon={<FaMagnifyingGlass />}
                                        className="text-[16px] xl:text-[24px] ml-2 xl:ml-3"
                                        onClick={() => {
                                            setIsEditing(true);
                                            setInputRoomId("");
                                        }}
                                    />
                                    <TooltipFFXIV>
                                        <TextFFXIV text={t("lobby.joinAnotherLobby")} />
                                    </TooltipFFXIV>
                                </div>
                            ) : (
                                <div className="group relative leave-lobby-block">
                                    <IconGlowWrapper
                                        icon={<GiExitDoor />}
                                        onClick={handleLeave}
                                        className="ml-3 text-[18px] xl:text-[26px]"
                                    />
                                    <TooltipFFXIV>
                                        <TextFFXIV text={t("lobby.leaveLobby")} />
                                    </TooltipFFXIV>
                                </div>
                            )}
                        </div>
                    </>
                ) : (
                    <>
                        <input
                            className="ml-3 px-2 max-w-[100px] xl:max-w-40 bg-transparent border-[1px] border-white shadow-[0_0_10px_1px_#2563eb,0_0_10px_1px_#2563eb_inset] rounded-md font-myriad font-bold outline-none cursor-text"
                            value={inputRoomId}
                            onChange={e => setInputRoomId(e.target.value)}
                            onKeyDown={handleInputKeyDown}
                            onBlur={handleInputBlur}
                            autoFocus
                            placeholder={t("lobby.enterLobbyId")}
                        />
                        <div className="group relative ml-2">
                            <IconGlowWrapper
                                icon={<FaCheck />}
                                onClick={() => {
                                    setIsEditing(false);
                                    handleJoin(inputRoomId);
                                }}
                                className="text-base xl:text-xl ml-1 xl:ml-3"
                            />
                            <TooltipFFXIV>
                                <TextFFXIV text={t("lobby.joinLobby")} />
                            </TooltipFFXIV>
                        </div>
                    </>
                )}
            </div>

        </LobbyPanel>
    )
}

export default LobbyInfo